import { Box, Divider, Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import type { PbjModelUpsertBody, PbjPrediction } from "../../../utils/models";
import { RecommendationRow } from "./RecommendationRow";
import type { PeriodMode } from "./RecommendationsSidebar";
import { type PlanItem, type RecommendationType, recColor } from "./recommendations.constants";

type Props = {
	prediction: PbjPrediction;
	census: number;
	visibleRecs: RecommendationType[];
	selectedById: Record<string, PlanItem>;
	spendMap: Record<string, number>;
	suggestedRecIds: Set<string>;
	// Bumped by every Suggest / Reset — replays the suggested sliders' land pulse.
	suggestNonce: number;
	selectedPlan: PlanItem[];
	periodMode: PeriodMode;
	draft: PbjModelUpsertBody;
	onAdd: (item: PlanItem) => void;
	onSlider: (rec: RecommendationType, newSpend: number) => void;
};

/**
 * The stack of lever cards, one per role with a known wage — or, when no role has a wage, the
 * "no wage data" empty state. Resolves each card's live spend and its suggested/inert standing.
 */
export const RecommendationLeverList: FC<Props> = ({
	prediction,
	census,
	visibleRecs,
	selectedById,
	spendMap,
	suggestedRecIds,
	suggestNonce,
	selectedPlan,
	periodMode,
	draft,
	onAdd,
	onSlider,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();

	if (visibleRecs.length === 0) {
		return (
			<Box sx={{ p: 3 }}>
				<Typography variant="caption" sx={{ color: "text.secondary" }}>
					{t("pbj_model.recommendations.no_wage_data")}
				</Typography>
			</Box>
		);
	}

	// In "Quarter to date" mode the math tooltips reflect catch-up math over the days left; in
	// "Quarter" mode, the full calendar quarter.
	const periodDays = periodMode === "quarter" ? prediction.quarterDays : prediction.days;
	const periodWeekendDays = periodMode === "quarter" ? prediction.quarterWeekendDays : prediction.weekendDays;

	return (
		<Stack divider={<Divider />} sx={{ flexGrow: 1 }}>
			{visibleRecs.map((rec) => {
				// DEFAULT: never taken — `visibleRecs` already drops levers with a null wage; this only narrows the type.
				const wage = prediction.deptWages[rec.wageKey] ?? 0;
				const selectedItem = selectedById[rec.id];
				// The plan item's value when selected, otherwise the local slider value.
				// DEFAULT: a lever that became visible with a new prediction renders once before the slider
				// hook's re-seed runs; 0 is exactly what that re-seed gives it.
				const currentSpend = selectedItem?.spend ?? spendMap[rec.id] ?? 0;
				const isSelected = selectedItem !== undefined;
				return (
					<RecommendationRow
						key={rec.id}
						rec={rec}
						wage={wage}
						color={recColor(theme, rec.colorKey)}
						selectedItem={selectedItem}
						currentSpend={currentSpend}
						// Positioned by the latest Suggest and not yet added — the wash, badge and land-pulse.
						isSuggested={suggestedRecIds.has(rec.id) && !isSelected && currentSpend !== 0}
						// Nothing dialed in either way and not in the plan (muted rail + fill).
						isInert={!isSelected && currentSpend === 0}
						suggestNonce={suggestNonce}
						prediction={prediction}
						census={census}
						selectedPlan={selectedPlan}
						periodMode={periodMode}
						periodDays={periodDays}
						periodWeekendDays={periodWeekendDays}
						draft={draft}
						onAdd={onAdd}
						onSlider={onSlider}
					/>
				);
			})}
		</Stack>
	);
};
